'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import AppImage from './ui/AppImage';
import Icon from './ui/AppIcon';
import './BeforeAfterSection.css';

const BeforeAfterSection = () => {
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
  const [sliderPos, setSliderPos] = useState(48);
  const [isDragging, setIsDragging] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.12 }
    );

    const els = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    if (els?.length) {
      els.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const getPos = useCallback((clientX) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(x);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      getPos(e.clientX);
    },
    [isDragging, getPos]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging) return;
      getPos(e.touches[0].clientX);
    },
    [isDragging, getPos]
  );

  const stopDrag = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', stopDrag);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stopDrag);
    };
  }, [handleMouseMove, handleTouchMove, stopDrag]);

  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={`${word}-${i}`}
        className="word-animate"
        style={{ animationDelay: `${startDelay + i * 40}ms` }}
      >
        {word}
        {i < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  return (
    <section id="before-after" ref={sectionRef} className="py-16 bg-kiln">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Section header */}
        <div
          className={`reveal-on-scroll mb-16 md:mb-20 ${revealed ? 'animate-reveal' : ''}`}>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-6 h-[1px] bg-teal" />
            <span className="font-mono-label text-[9px] font-medium text-teal uppercase tracking-[0.5em]">
              Refurbished · Before & After
            </span>
          </div>
          <div className="max-w-3xl">
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
              {splitWords("Same device.", 100)}{' '}
              <span className="text-teal inline-block">
                {splitWords("Professional renewal.", 200)}
              </span>
            </h2>
          </div>
        </div>

        {/* Before/After Slider */}
        <div
          className={`reveal-on-scroll animate-on-scroll anim-in delay-100 ${revealed ? 'animate-reveal stagger-2' : ''}`}>

          <div
            ref={sliderRef}
            className="ba-slider rounded-2xl overflow-hidden select-none"
            style={{ 
              aspectRatio: '2 / 1', 
              height: 'auto',
              maxHeight: '620px', 
              cursor: isDragging ? 'grabbing' : 'ew-resize' 
            }}
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}>

            {/* BEFORE layer (Background) */}
            <AppImage
              src="/images/before.webp"
              alt="Laptop before renovation process - worn and dusty"
              fill
              className="object-cover w-full h-full"
              style={{ objectPosition: 'center', objectFit: 'cover' }}
              onError={(e) => { e.target.src = '/images/before.webp'; }} />

            {/* AFTER layer (Overlay) */}
            <div
              className="ba-after"
              style={{ width: `${sliderPos}%` }}>

              <AppImage
                src="/images/after.jpg"
                alt="Laptop after Nexawave restoration - polished and like-new"
                fill
                className="object-cover w-full h-full"
                style={{ 
                  width: `${100 / (sliderPos / 100)}%`, 
                  height: '100%',
                  maxWidth: 'none',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block'
                }}
                onError={(e) => { e.target.src = '/images/after.webp'; }} />

              <div className="absolute top-4 left-4 z-10 flex">
                <span
                  className="text-white text-[9px] uppercase tracking-[0.25em] px-3 py-1.5 font-medium"
                  style={{ backgroundColor: '#0063B8' }}
                >
                  AFTER
                </span>

                <span
                  className="w-[6px]"
                  style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}
                />
              </div>
            </div>

            {/* Divider */}
            <div
              className="ba-divider"
              style={{ left: `${sliderPos}%` }}>

              <div className="ba-handle">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="white">
                  <path d="M7 4L2 10L7 16M13 4L18 10L13 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
            </div>
          </div>

          {/* Instruction */}
          <div className="mt-4 flex items-center justify-center gap-2 opacity-50">
            <Icon name="ArrowsRightLeftIcon" size={14} className="text-cement" />
            <span className="font-mono-label text-[9px] text-cement uppercase tracking-widest">
              Drag to compare renewal quality
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;